import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useYacht } from "../../context/YachtContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import FileUploader from "../../components/FileUploader";
import ProfileForm from "../../components/crew/ProfileForm";
import {
  DOC_KINDS, PAY_TYPES, REHIRE, CONTRACT_TYPES, PAYMENT_METHODS,
  PAY_QTY_TYPES, PAY_UNITS, payUnitFor, paymentMethodLabel, payTypeLabel,
  titleCase, STATUS_STYLE, statusLabel,
} from "../../lib/crewHr";
import {
  Loader2, RefreshCw, FileText, Trash2, AlertTriangle, ChevronLeft, ShieldCheck, Plus,
} from "lucide-react";
import { APP_MODULES } from "../../lib/modules";

const fmtErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const FieldRow = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
    {children}
  </div>
);

/* Access tab — real per-module permissions on this yacht. Checked = the
 * member sees and can use the menu item; unchecked = hidden + API blocked. */
function AccessTab({ bundle, onSaved }) {
  const editable = bundle.can_edit_access;
  const memberId = bundle.member?.member_id;
  const [blocked, setBlocked] = useState(() => new Set(bundle.member?.blocked_modules || []));
  const [busy, setBusy] = useState(false);

  const toggle = (key) => setBlocked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/yacht-members/${memberId}/modules`, { blocked_modules: [...blocked] });
      toast.success("Access saved — the menu updates the next time they load the app");
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="hr-access-tab">
      <div className="flex items-start gap-2 mb-4">
        <ShieldCheck className="h-4 w-4 mt-0.5 text-zinc-500" />
        <p className="text-sm text-zinc-600">
          Checked items are visible and usable for <strong>{bundle.member?.name || bundle.member?.email}</strong> on this yacht.
          Unchecked items disappear from their menu and access is blocked. Dashboard and personal settings always stay available.
        </p>
      </div>
      {!editable && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="hr-access-readonly">
          Only an Owner (anyone below owner) or a Manager (Captain and below) can change this member's access.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {APP_MODULES.map((m) => (
          <label
            key={m.key}
            className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
              blocked.has(m.key) ? "border-zinc-200 bg-zinc-50 text-zinc-400" : "border-zinc-200 bg-white text-zinc-800"}
              ${editable ? "cursor-pointer hover:border-zinc-300" : "opacity-70 cursor-not-allowed"}`}
            data-testid={`module-toggle-${m.key}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 accent-zinc-900"
              checked={!blocked.has(m.key)}
              disabled={!editable}
              onChange={() => toggle(m.key)}
              data-testid={`module-checkbox-${m.key}`}
            />
            {m.label}
          </label>
        ))}
      </div>
      {editable && (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-ink hover:bg-black" data-testid="save-access">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save access
          </Button>
        </div>
      )}
      <LimitsSection bundle={bundle} editable={editable} memberId={memberId} />
    </div>
  );
}

/* Approval limits — senior roles only (Manager / Captain / First Officer).
 * Empty amount = no limit. Actions above a limit are blocked and the
 * owner/managers are notified. Owners are never limited. */
const LIMIT_ROLES = new Set(["manager", "captain", "first_officer"]);
function LimitsSection({ bundle, editable, memberId }) {
  const { activeYacht } = useYacht();
  const ccy = activeYacht?.currency || "";
  const role = bundle.member?.role;
  const init = bundle.member?.limits || {};
  const [lim, setLim] = useState({
    quote_approval_limit: init.quote_approval_limit ?? "",
    pettycash_approval_limit: init.pettycash_approval_limit ?? "",
    invoice_payment_limit: init.invoice_payment_limit ?? "",
    final_approval_allowed: init.final_approval_allowed !== false,
  });
  const [busy, setBusy] = useState(false);
  if (!LIMIT_ROLES.has(role)) return null;

  const upd = (k, v) => setLim((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/yacht-members/${memberId}/limits`, {
        quote_approval_limit: lim.quote_approval_limit === "" ? null : Number(lim.quote_approval_limit),
        pettycash_approval_limit: lim.pettycash_approval_limit === "" ? null : Number(lim.pettycash_approval_limit),
        invoice_payment_limit: lim.invoice_payment_limit === "" ? null : Number(lim.invoice_payment_limit),
        final_approval_allowed: !!lim.final_approval_allowed,
      });
      toast.success("Limits saved");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const FIELDS = [
    { key: "quote_approval_limit", label: "Supplier quote approval limit", hint: "Max amount they may accept for a supplier quote" },
    { key: "pettycash_approval_limit", label: "Petty cash approval limit", hint: "Max amount per petty-cash expense they can approve" },
    { key: "invoice_payment_limit", label: "Invoice payment limit", hint: "Max invoice amount they can mark as paid" },
  ];

  return (
    <div className="mt-6 border-t border-zinc-200 pt-5" data-testid="hr-limits-section">
      <h3 className="text-sm font-semibold text-zinc-800 mb-1">Approval limits</h3>
      <p className="text-xs text-zinc-500 mb-4">
        {ccy ? `Amounts in ${ccy} (the yacht's currency).` : "Amounts in the yacht's currency — set it in Yacht details to label these fields."} Empty = no limit. Anything above a limit is blocked
        and the owner/managers are notified. Owners are never limited.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <div className="text-xs font-medium text-zinc-600 mb-1">{f.label}{ccy ? ` (${ccy})` : ""}</div>
            <div className="relative">
              <Input type="number" min="0" placeholder="No limit" value={lim[f.key]}
                disabled={!editable}
                className={ccy ? "pr-12" : ""}
                onChange={(e) => upd(f.key, e.target.value)}
                data-testid={`limit-${f.key}`} />
              {ccy && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none" data-testid={`limit-ccy-${f.key}`}>{ccy}</span>
              )}
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">{f.hint}</p>
          </div>
        ))}
      </div>
      <label className={`mt-3 flex items-center gap-2.5 rounded-lg border border-zinc-200 px-3 py-2.5 text-sm w-fit ${editable ? "cursor-pointer hover:border-zinc-300" : "opacity-70 cursor-not-allowed"}`} data-testid="limit-final-approval-toggle">
        <input type="checkbox" className="h-4 w-4 accent-zinc-900"
          checked={lim.final_approval_allowed}
          disabled={!editable}
          onChange={(e) => upd("final_approval_allowed", e.target.checked)}
          data-testid="limit-final-approval-checkbox" />
        May give final approval on completed supplier tasks
      </label>
      {editable && (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-ink hover:bg-black" data-testid="save-limits">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save limits
          </Button>
        </div>
      )}
    </div>
  );
}

export default function CrewHR() {
  const { userId } = useParams();
  const [params] = useSearchParams();
  const yachtId = params.get("yacht_id");
  const navigate = useNavigate();
  const { activeYacht } = useYacht();

  // The HR record belongs to a specific yacht (yacht_id in the URL). If the
  // user switches the active yacht via the yacht selector, this record is
  // stale — send them to the Crew & Network page of the new yacht.
  useEffect(() => {
    if (activeYacht?.id && yachtId && activeYacht.id !== yachtId) {
      navigate("/app/crew", { replace: true });
    }
  }, [activeYacht?.id, yachtId, navigate]);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);     // editable yacht copy
  const [emp, setEmp] = useState({});
  const [allowanceTypes, setAllowanceTypes] = useState([]); // yacht-level custom types

  // Salary section helpers — entries live on emp.salaries, saved with Save employment.
  // Legacy entries had a single allowance_type/allowance_amount pair; normalise
  // them into the `allowances` list on load.
  const normSalaries = (list) => (list || []).map((s) => ({
    ...s,
    allowances: s.allowances || (s.allowance_type || s.allowance_amount
      ? [{ type: s.allowance_type || "", amount: s.allowance_amount || "" }]
      : []),
  }));
  const updSalary = (i, k, v) => setEmp((p) => {
    const arr = [...(p.salaries || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...p, salaries: arr };
  });
  const addSalary = () => setEmp((p) => ({
    ...p,
    salaries: [...(p.salaries || []), { amount: "", net_salary: "", period: "monthly", start_date: "", allowances: [] }],
  }));
  const delSalary = (i) => setEmp((p) => ({ ...p, salaries: (p.salaries || []).filter((_, j) => j !== i) }));
  const updAllowance = (i, j, k, v) => setEmp((p) => {
    const arr = [...(p.salaries || [])];
    const al = [...(arr[i].allowances || [])];
    al[j] = { ...al[j], [k]: v };
    arr[i] = { ...arr[i], allowances: al };
    return { ...p, salaries: arr };
  });
  const addAllowance = (i) => setEmp((p) => {
    const arr = [...(p.salaries || [])];
    arr[i] = { ...arr[i], allowances: [...(arr[i].allowances || []), { type: "food", amount: "" }] };
    return { ...p, salaries: arr };
  });
  const delAllowance = (i, j) => setEmp((p) => {
    const arr = [...(p.salaries || [])];
    arr[i] = { ...arr[i], allowances: (arr[i].allowances || []).filter((_, x) => x !== j) };
    return { ...p, salaries: arr };
  });

  // Identity (name/phone/email) always mirrors the user's account — these are
  // auto-synced and shown read-only. Other HR fields stay empty until the
  // senior fills them or runs a one-time "Sync from user profile".
  const identityFrom = (b) => ({
    full_name: b?.member?.name || "",
    phone: b?.member?.phone || "",
    email: b?.member?.email || "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crew/${userId}/hr?yacht_id=${yachtId}`);
      setBundle(data);
      setProfile({ ...(data.yacht_profile || {}), ...identityFrom(data) });
      setEmp({ ...(data.employment || {}), salaries: normSalaries(data.employment?.salaries) });
      if (data.can_admin) {
        try {
          const { data: at } = await api.get(`/crew/allowance-types?yacht_id=${yachtId}`);
          setAllowanceTypes(at.types || []);
        } catch { setAllowanceTypes([]); }
      }
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  }, [userId, yachtId]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    if (!window.confirm("Pull this crew member's latest profile and overwrite the yacht copy? Your manual edits to the copy will be replaced.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/crew/${userId}/sync?yacht_id=${yachtId}`);
      setProfile({ ...data.yacht_profile, ...identityFrom(bundle) });
      setBundle((b) => ({ ...b, yacht_profile: data.yacht_profile, synced_at: data.synced_at, profile_pending_sync: false }));
      toast.success("Synced from user profile");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSyncing(false); }
  };

  const saveProfile = async () => {
    try {
      const { data } = await api.put(`/crew/${userId}/yacht-profile?yacht_id=${yachtId}`, profile);
      setProfile(data); toast.success("Yacht copy saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const saveEmp = async () => {
    try {
      const base = ["food", "housing", "petrol"];
      const knownLower = new Set([...base, ...allowanceTypes.map((t) => t.toLowerCase())]);
      const newTypes = [];
      const clean = {
        ...emp,
        salaries: (emp.salaries || []).map((s) => ({
          ...s,
          allowances: (s.allowances || []).map((a) => {
            const t = a.type === "__new" ? "" : (a.type || "").trim();
            if (t && !knownLower.has(t.toLowerCase()) && !newTypes.some((x) => x.toLowerCase() === t.toLowerCase())) newTypes.push(t);
            return { ...a, type: t };
          }),
        })),
      };
      const { data } = await api.put(`/crew/${userId}/employment?yacht_id=${yachtId}`, clean);
      // Persist any newly typed allowance types for future entries on this yacht.
      for (const t of newTypes) {
        try {
          const { data: at } = await api.post(`/crew/allowance-types?yacht_id=${yachtId}`, { type: t });
          setAllowanceTypes(at.types || []);
        } catch { /* non-blocking */ }
      }
      setEmp({ ...data, salaries: normSalaries(data.salaries) }); toast.success("Saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (loading || !bundle) {
    return <div className="flex items-center gap-2 text-zinc-500 p-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading crew record…</div>;
  }
  const canAdmin = bundle.can_admin;
  const alerts = bundle.cert_alerts || [];

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="crew-hr-page">
      <Link to="/app/crew" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 mb-3"><ChevronLeft className="h-4 w-4" />Back to crew</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl">{profile?.full_name || bundle.member.email}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline">{titleCase(bundle.member.role)}</Badge>
            {bundle.synced_at
              ? <span className="text-xs text-zinc-400">Synced {String(bundle.synced_at).slice(0, 10)}</span>
              : <span className="text-xs text-amber-600">Never synced</span>}
          </div>
        </div>
        {!bundle.member.is_ex_crew && !bundle.synced_at && (
          <Button onClick={sync} disabled={syncing || !bundle.master_exists} variant="outline" data-testid="sync-profile-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {bundle.master_exists ? "Sync from user profile" : "Crew has no profile yet"}
          </Button>
        )}
      </div>

      {bundle.member.is_ex_crew && (
        <div className="mb-4 rounded-lg border border-zinc-300 bg-zinc-50 p-3 text-sm text-zinc-600" data-testid="hr-ex-crew-banner">
          This crew member has been removed from this yacht (<span className="font-medium">Ex-Crew</span>). Their HR data is retained and read-only here. Re-invite them from the crew page to make changes again.
        </div>
      )}

      {!bundle.member.is_ex_crew && bundle.profile_pending_sync && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="hr-profile-pending-sync">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <RefreshCw className="h-4 w-4" />
            This crew member updated their profile since your last sync.
          </div>
          <Button onClick={sync} disabled={syncing || !bundle.master_exists} size="sm" className="bg-ink hover:bg-black" data-testid="pending-sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync now
          </Button>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center gap-2" data-testid="hr-cert-alerts">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          {alerts.map((a, i) => <Badge key={i} className={`${STATUS_STYLE[a.status]} border`}>{a.cert_label}: {statusLabel(a.status, a.days_left)}</Badge>)}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="max-w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          {canAdmin && <TabsTrigger value="employment" data-testid="tab-employment">Employment</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          {canAdmin && <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding</TabsTrigger>}
          {canAdmin && <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>}
          {canAdmin && <TabsTrigger value="access" data-testid="tab-access">Access</TabsTrigger>}
        </TabsList>

        {/* Profile (yacht copy). Name/phone/email auto-sync from the user's
            account (read-only); other fields stay empty until filled or pulled
            via the one-time "Sync from user profile". */}
        <TabsContent value="profile" className="mt-4">
          <ProfileForm value={profile} set={setProfile} yachtId={yachtId} lockIdentity identityHint="Auto-synced from the user's account" disabled={bundle.member.is_ex_crew} />
          {!bundle.member.is_ex_crew && (
            <div className="mt-4 flex justify-end"><Button onClick={saveProfile} className="bg-ink hover:bg-black" data-testid="save-yacht-profile">Save yacht copy</Button></div>
          )}
        </TabsContent>

        {/* Employment */}
        {canAdmin && (
          <TabsContent value="employment" className="mt-4">
            <div className="rounded-xl border border-zinc-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Internal crew ID">
                <Input value={emp.internal_crew_id || ""} readOnly disabled placeholder="Auto-assigned on save" className="bg-zinc-50" data-testid="emp-internal-id" />
              </FieldRow>
              <FieldRow label="Contract type">
                <select className="h-9 w-full rounded-md border border-input px-2 text-sm" value={emp.contract_type || ""} onChange={(e) => setEmp((p) => ({ ...p, contract_type: e.target.value }))} data-testid="emp-contract-type">
                  <option value="">—</option>{CONTRACT_TYPES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Start date"><Input type="date" value={emp.start_date || ""} onChange={(e) => setEmp((p) => ({ ...p, start_date: e.target.value }))} /></FieldRow>
              <FieldRow label="End date"><Input type="date" value={emp.end_date || ""} onChange={(e) => setEmp((p) => ({ ...p, end_date: e.target.value }))} /></FieldRow>
              <FieldRow label="Payment methods">
                <div className="flex flex-wrap gap-4 pt-1.5">
                  {PAYMENT_METHODS.map((pm) => (
                    <label key={pm.key} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={(emp.payment_methods || []).includes(pm.key)}
                        onChange={(e) => setEmp((p) => {
                          const cur = new Set(p.payment_methods || []);
                          if (e.target.checked) cur.add(pm.key); else cur.delete(pm.key);
                          return { ...p, payment_methods: Array.from(cur) };
                        })} data-testid={`emp-pay-${pm.key}`} /> {pm.label}
                    </label>
                  ))}
                </div>
              </FieldRow>
              <FieldRow label="Rehire status">
                <select className="h-9 w-full rounded-md border border-input px-2 text-sm" value={emp.rehire_status || ""} onChange={(e) => setEmp((p) => ({ ...p, rehire_status: e.target.value }))} data-testid="emp-rehire">
                  <option value="">—</option>{REHIRE.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </FieldRow>
              {[["health_insurance_provided", "Health insurance provided"], ["visa_handled_by_company", "Visa handled by company"], ["accommodation_provided", "Accommodation provided"], ["travel_costs_covered", "Travel costs covered"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm" data-testid={`emp-${k}`}><input type="checkbox" checked={!!emp[k]} onChange={(e) => setEmp((p) => ({ ...p, [k]: e.target.checked }))} /> {l}</label>
              ))}
            </div>

            {/* Salary — salary agreements over time incl. allowances. */}
            <div className="rounded-xl border border-zinc-200 bg-white p-5 mt-4" data-testid="salary-section">
              <h3 className="font-display text-base">Salary</h3>
              <p className="text-xs text-zinc-500 mb-4">Salary agreements over time.</p>
              {(emp.salaries || []).length === 0 && (
                <p className="text-sm text-zinc-400 mb-1" data-testid="salary-empty">No salary entries yet.</p>
              )}
              <div className="space-y-3">
                {(emp.salaries || []).map((s, i) => {
                  const n = (v) => Number(v) || 0;
                  const totalGross = n(s.amount) + (s.allowances || []).reduce((t, a) => t + n(a.amount), 0);
                  const KNOWN = ["food", "housing", "petrol", ...allowanceTypes];
                  return (
                  <div key={i} className="relative rounded-lg border border-zinc-100 bg-zinc-50/60 p-3 pr-9 space-y-3" data-testid={`salary-entry-${i}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FieldRow label="Gross Salary">
                        <Input type="number" value={s.amount ?? ""} onChange={(e) => updSalary(i, "amount", e.target.value)} data-testid={`salary-amount-${i}`} />
                      </FieldRow>
                      <FieldRow label="Salary period">
                        <select className="h-9 w-full rounded-md border border-input px-2 text-sm bg-white" value={s.period || ""} onChange={(e) => updSalary(i, "period", e.target.value)} data-testid={`salary-period-${i}`}>
                          <option value="">—</option>
                          <option value="monthly">per month</option>
                          <option value="weekly">per week</option>
                          <option value="daily">per day</option>
                          <option value="hourly">per hour</option>
                        </select>
                      </FieldRow>
                    </div>

                    {/* Allowances — multiple per salary agreement */}
                    <div>
                      <div className="text-xs font-medium text-zinc-600 mb-1.5">Allowances</div>
                      <div className="space-y-2">
                        {(s.allowances || []).map((a, j) => {
                          const isCustom = a.type === "__new" || (a.type && !KNOWN.includes(a.type));
                          return (
                            <div key={j} className="flex flex-wrap items-center gap-2" data-testid={`salary-${i}-allowance-${j}`}>
                              <select className="h-9 rounded-md border border-input px-2 text-sm bg-white w-36"
                                value={isCustom ? "__new" : (a.type || "")}
                                onChange={(e) => updAllowance(i, j, "type", e.target.value)}
                                data-testid={`salary-${i}-allowance-type-${j}`}>
                                <option value="">—</option>
                                <option value="food">Food</option>
                                <option value="housing">Housing</option>
                                <option value="petrol">Petrol</option>
                                {allowanceTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                                <option value="__new">+ New</option>
                              </select>
                              {isCustom && (
                                <Input className="w-36" placeholder="Type name" value={a.type === "__new" ? "" : a.type}
                                  onChange={(e) => updAllowance(i, j, "type", e.target.value || "__new")}
                                  data-testid={`salary-${i}-allowance-custom-${j}`} />
                              )}
                              <Input type="number" className="w-32" placeholder="Amount" value={a.amount ?? ""}
                                onChange={(e) => updAllowance(i, j, "amount", e.target.value)}
                                data-testid={`salary-${i}-allowance-amount-${j}`} />
                              <button type="button" className="text-red-400 hover:text-red-600" onClick={() => delAllowance(i, j)} data-testid={`salary-${i}-allowance-del-${j}`}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="mt-1.5 text-zinc-600" onClick={() => addAllowance(i)} data-testid={`salary-${i}-add-allowance`}>
                        <Plus className="h-3.5 w-3.5 mr-1" />Add allowance
                      </Button>
                    </div>

                    {/* Salary start date + Total Gross Salary on one row, Net Salary below */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                      <FieldRow label="Salary start date">
                        <Input type="date" value={s.start_date || ""} onChange={(e) => updSalary(i, "start_date", e.target.value)} data-testid={`salary-start-${i}`} />
                      </FieldRow>
                      <div className="text-sm pb-2" data-testid={`salary-total-gross-${i}`}>
                        <span className="text-zinc-500">Total Gross Salary: </span>
                        <span className="font-semibold">{totalGross.toLocaleString()}</span>
                        {s.period && <span className="text-zinc-400 text-xs"> {({ monthly: "per month", weekly: "per week", daily: "per day", hourly: "per hour" })[s.period] || ""}</span>}
                      </div>
                    </div>

                    <div className="border-t border-zinc-200 pt-3">
                      <div className="w-full sm:w-64">
                        <FieldRow label="Net Salary">
                          <Input type="number" value={(s.net_salary === "" || s.net_salary == null) ? (totalGross || "") : s.net_salary}
                            onChange={(e) => updSalary(i, "net_salary", e.target.value)} data-testid={`salary-net-${i}`} />
                          <p className="text-[10px] text-zinc-400 mt-1">Defaults to Total Gross Salary — edit if the payroll provider calculates a different net.</p>
                        </FieldRow>
                      </div>
                    </div>

                    <button type="button" className="absolute top-2 right-2 text-red-400 hover:text-red-600" onClick={() => delSalary(i)} data-testid={`salary-del-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Button variant="outline" onClick={addSalary} data-testid="add-salary-btn">
                  <Plus className="h-4 w-4 mr-2" />Add new salary
                </Button>
                <Button onClick={saveEmp} className="bg-ink hover:bg-black" data-testid="save-salary-btn">Save Salary</Button>
              </div>
            </div>

            {/* Bank & personal address — yacht-private, HR managers only. */}
            <div className="rounded-xl border border-zinc-200 bg-white p-5 mt-4" data-testid="bank-details-section">
              <h3 className="font-display text-base">Bank details</h3>
              <p className="text-xs text-zinc-500 mb-4">Bank &amp; personal address used for payroll. Yacht-private — only authorized HR managers can see or edit this. It is never stored on the crew member's own profile.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldRow label="Account holder"><Input value={emp.bank_account_holder || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_account_holder: e.target.value }))} data-testid="bank-account-holder" /></FieldRow>
                <FieldRow label="Bank name"><Input value={emp.bank_name || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_name: e.target.value }))} data-testid="bank-name" /></FieldRow>
                <FieldRow label="IBAN"><Input className="font-mono text-sm" value={emp.bank_iban || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_iban: e.target.value }))} data-testid="bank-iban" /></FieldRow>
                <FieldRow label="BIC / SWIFT"><Input className="font-mono text-sm" value={emp.bank_bic_swift || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_bic_swift: e.target.value }))} data-testid="bank-bic-swift" /></FieldRow>
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-5 mb-2">Personal address</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldRow label="Address line 1"><Input value={emp.bank_address_line1 || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_address_line1: e.target.value }))} data-testid="bank-address-line1" /></FieldRow>
                <FieldRow label="Address line 2"><Input value={emp.bank_address_line2 || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_address_line2: e.target.value }))} data-testid="bank-address-line2" /></FieldRow>
                <FieldRow label="City"><Input value={emp.bank_city || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_city: e.target.value }))} data-testid="bank-city" /></FieldRow>
                <FieldRow label="Postcode"><Input value={emp.bank_postcode || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_postcode: e.target.value }))} data-testid="bank-postcode" /></FieldRow>
                <FieldRow label="Country"><Input value={emp.bank_country || ""} onChange={(e) => setEmp((p) => ({ ...p, bank_country: e.target.value }))} data-testid="bank-country" /></FieldRow>
              </div>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-5 mt-4">
              <h3 className="font-display text-base mb-4">Internal performance notes</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[["manager_rating", "Manager"], ["reliability_score", "Reliability"], ["punctuality", "Punctuality"], ["work_quality", "Work quality"]].map(([k, l]) => (
                  <FieldRow key={k} label={`${l} (1-5)`}><Input type="number" min="1" max="5" value={emp[k] ?? ""} onChange={(e) => setEmp((p) => ({ ...p, [k]: e.target.value ? Number(e.target.value) : null }))} /></FieldRow>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldRow label="Strengths"><Textarea rows={2} value={emp.strengths || ""} onChange={(e) => setEmp((p) => ({ ...p, strengths: e.target.value }))} /></FieldRow>
                <FieldRow label="Weaknesses"><Textarea rows={2} value={emp.weaknesses || ""} onChange={(e) => setEmp((p) => ({ ...p, weaknesses: e.target.value }))} /></FieldRow>
                <FieldRow label="Captain feedback"><Textarea rows={2} value={emp.captain_feedback || ""} onChange={(e) => setEmp((p) => ({ ...p, captain_feedback: e.target.value }))} /></FieldRow>
                <FieldRow label="Guest feedback"><Textarea rows={2} value={emp.guest_feedback || ""} onChange={(e) => setEmp((p) => ({ ...p, guest_feedback: e.target.value }))} /></FieldRow>
                <FieldRow label="Incident notes"><Textarea rows={2} value={emp.incident_notes || ""} onChange={(e) => setEmp((p) => ({ ...p, incident_notes: e.target.value }))} /></FieldRow>
              </div>
            </div>
            <div className="mt-4 flex justify-end"><Button onClick={saveEmp} className="bg-ink hover:bg-black" data-testid="save-employment">Save employment</Button></div>
          </TabsContent>
        )}

        {/* Access — real per-module permissions on this yacht */}
        {canAdmin && (
          <TabsContent value="access" className="mt-4">
            <AccessTab key={bundle.member?.member_id || userId} bundle={bundle} onSaved={load} />
          </TabsContent>
        )}

        {/* Onboarding — what was handed to this crew member */}
        {canAdmin && (
          <TabsContent value="onboarding" className="mt-4">
            <div className="rounded-xl border border-zinc-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Key / card assigned"><Input value={emp.key_card_assigned || ""} onChange={(e) => setEmp((p) => ({ ...p, key_card_assigned: e.target.value }))} /></FieldRow>
              <FieldRow label="Cabin assigned"><Input value={emp.cabin_assigned || ""} onChange={(e) => setEmp((p) => ({ ...p, cabin_assigned: e.target.value }))} /></FieldRow>
              <FieldRow label="Inventory responsibility"><Input value={emp.inventory_responsibility || ""} onChange={(e) => setEmp((p) => ({ ...p, inventory_responsibility: e.target.value }))} /></FieldRow>
              <FieldRow label="Equipment issued (comma separated)"><Input value={(emp.equipment_issued || []).join(", ")} onChange={(e) => setEmp((p) => ({ ...p, equipment_issued: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} /></FieldRow>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!emp.uniform_issued} onChange={(e) => setEmp((p) => ({ ...p, uniform_issued: e.target.checked }))} /> Uniform issued</label>
              <div className="sm:col-span-2 flex justify-end"><Button onClick={saveEmp} className="bg-ink hover:bg-black" data-testid="save-onboarding">Save onboarding</Button></div>
            </div>
          </TabsContent>
        )}

        {/* Documents */}
        <TabsContent value="documents" className="mt-4">
          <DocumentsTab userId={userId} yachtId={yachtId} docs={bundle.documents} onChange={load} />
        </TabsContent>

        {/* Payroll */}
        {canAdmin && (
          <TabsContent value="payroll" className="mt-4">
            <PayrollTab userId={userId} yachtId={yachtId} entries={bundle.paylog || []} onChange={load} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function DocumentsTab({ userId, yachtId, docs, onChange }) {
  const [kind, setKind] = useState("contract");
  const [expiry, setExpiry] = useState("");
  const add = async (path, rec) => {
    try {
      await api.post(`/crew/${userId}/documents?yacht_id=${yachtId}`, { kind, name: rec?.original_filename || "Document", file_path: path, expiry_date: expiry || null });
      toast.success("Document added"); setExpiry(""); onChange();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/crew/documents/${id}`); onChange(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const expiryBadge = (d) => {
    if (!d.expiry_date) return null;
    const days = Math.floor((new Date(d.expiry_date) - new Date()) / 86400000);
    const cls = days < 0 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600";
    return <Badge className={`text-[10px] ${cls}`} data-testid={`doc-expiry-${d.id}`}>{days < 0 ? `Expired ${d.expiry_date}` : `Expires ${d.expiry_date}`}</Badge>;
  };
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Type</label>
          <select className="h-9 rounded-md border border-input px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="doc-kind">
            {DOC_KINDS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Expiry date (optional)</label>
          <Input type="date" className="h-9 w-40" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="doc-expiry-input" />
        </div>
        <FileUploader folder="crew_doc" yacht_id={yachtId} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" label="Upload document" testid="doc-upload" onUploaded={add} />
      </div>
      {docs.length === 0 ? <p className="text-sm text-zinc-400">No documents stored.</p> : (
        <div className="divide-y divide-zinc-100">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between py-2.5" data-testid={`doc-${d.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-zinc-500" />
                <a className="text-blue-600 hover:underline" target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_path}`}>{d.name || "Document"}</a>
                <Badge variant="outline" className="text-[10px]">{titleCase(d.kind)}</Badge>
                {expiryBadge(d)}
              </div>
              <button className="text-red-500" onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PayMethodChecks({ value, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-4 h-9">
      {PAYMENT_METHODS.map((pm) => (
        <label key={pm.key} className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={value === pm.key}
            onChange={(e) => onChange(e.target.checked ? pm.key : "")}
            data-testid={`${testidPrefix}-${pm.key}`} /> {pm.label}
        </label>
      ))}
    </div>
  );
}

function PayrollTab({ userId, yachtId, entries, onChange }) {
  const blank = { date: "", type: "daywork", quantity: "", unit: "days", period: "", amount: "", additional_amount: "", additional_type: "", deduct_amount: "", deduct_type: "", payment_method: "", note: "" };
  const [row, setRow] = useState(blank);
  const isQty = PAY_QTY_TYPES.includes(row.type);
  const setType = (type) => setRow((p) => ({ ...p, type, unit: payUnitFor(type) }));

  // ── Report: total salary received + open loan ──
  const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);
  const salaryEntries = entries.filter((e) => e.type === "salary");
  const salaryNet = salaryEntries.reduce((s, e) => s + num(e.amount) + num(e.additional_amount) - num(e.deduct_amount), 0);
  const salaryMonths = new Set(salaryEntries.map((e) => e.period || (e.date || "").slice(0, 7))).size;
  const firstPeriod = salaryEntries.map((e) => e.period || (e.date || "").slice(0, 10)).filter(Boolean).sort()[0];
  const loanGiven = entries.filter((e) => ["loan", "advance"].includes(e.type)).reduce((s, e) => s + num(e.amount), 0);
  const loanRepaid = entries.filter((e) => ["loan", "advance"].includes(e.deduct_type || "")).reduce((s, e) => s + num(e.deduct_amount), 0);
  const loanOpen = loanGiven - loanRepaid;

  const add = async () => {
    if (!row.date) return toast.error("Pick a date");
    try {
      await api.post(`/crew/${userId}/paylog?yacht_id=${yachtId}`, {
        date: row.date, type: row.type,
        quantity: isQty && row.quantity ? Number(row.quantity) : null,
        unit: isQty ? row.unit : null,
        period: isQty ? null : (row.period || null),
        amount: row.amount ? Number(row.amount) : null,
        additional_amount: row.additional_amount ? Number(row.additional_amount) : null,
        additional_type: row.additional_amount ? (row.additional_type || null) : null,
        deduct_amount: row.deduct_amount ? Number(row.deduct_amount) : null,
        deduct_type: row.deduct_amount ? (row.deduct_type || null) : null,
        payment_method: row.payment_method || null,
        note: row.note || null,
      });
      setRow(blank); onChange();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/crew/paylog/${id}`); onChange(); } catch (e) { toast.error(fmtErr(e)); } };
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5">
      {/* Report */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="payroll-report">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700">Total salary received</div>
          <div className="text-xl font-semibold text-emerald-900" data-testid="payroll-total-salary">{salaryNet.toLocaleString()}</div>
          <div className="text-xs text-emerald-800/70">
            {salaryMonths ? `${salaryMonths} month${salaryMonths === 1 ? "" : "s"}${firstPeriod ? ` · since ${firstPeriod}` : ""}` : "No salary entries yet"}
          </div>
        </div>
        <div className={`rounded-lg border px-4 py-3 ${loanOpen > 0 ? "border-red-200 bg-red-50/60" : "border-zinc-200 bg-zinc-50/60"}`}>
          <div className={`text-[11px] uppercase tracking-wider ${loanOpen > 0 ? "text-red-700" : "text-zinc-500"}`}>Open loan / advance</div>
          <div className={`text-xl font-semibold ${loanOpen > 0 ? "text-red-900" : "text-zinc-700"}`} data-testid="payroll-open-loan">{loanOpen.toLocaleString()}</div>
          <div className={`text-xs ${loanOpen > 0 ? "text-red-800/70" : "text-zinc-500"}`}>
            given {loanGiven.toLocaleString()} · repaid via deductions {loanRepaid.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 items-end mb-5">
        <FieldRow label="Date"><Input type="date" value={row.date} onChange={(e) => setRow((p) => ({ ...p, date: e.target.value }))} data-testid="pay-date" /></FieldRow>
        <FieldRow label="Type">
          <select className="h-9 w-full rounded-md border border-input px-2 text-sm" value={row.type} onChange={(e) => setType(e.target.value)} data-testid="pay-type">
            {PAY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </FieldRow>
        {isQty ? (
          <>
            <FieldRow label="Quantity"><Input type="number" placeholder="e.g. 10" value={row.quantity} onChange={(e) => setRow((p) => ({ ...p, quantity: e.target.value }))} data-testid="pay-quantity" /></FieldRow>
            <FieldRow label="Unit">
              <select className="h-9 w-full rounded-md border border-input px-2 text-sm" value={row.unit} onChange={(e) => setRow((p) => ({ ...p, unit: e.target.value }))} data-testid="pay-unit">
                {PAY_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </FieldRow>
          </>
        ) : (
          <FieldRow label="Month / Year"><Input type="month" value={row.period} onChange={(e) => setRow((p) => ({ ...p, period: e.target.value }))} data-testid="pay-period" /></FieldRow>
        )}
        <FieldRow label="Salary amount"><Input type="number" placeholder="Amount" value={row.amount} onChange={(e) => setRow((p) => ({ ...p, amount: e.target.value }))} data-testid="pay-amount" /></FieldRow>
        <FieldRow label="Additional">
          <div className="flex gap-2">
            <Input type="number" placeholder="Amount" value={row.additional_amount} onChange={(e) => setRow((p) => ({ ...p, additional_amount: e.target.value }))} data-testid="pay-additional-amount" />
            <select className="h-9 rounded-md border border-input px-2 text-sm" value={row.additional_type} onChange={(e) => setRow((p) => ({ ...p, additional_type: e.target.value }))} data-testid="pay-additional-type">
              <option value="">Type</option>
              <option value="bonus">Bonus</option>
              <option value="expenses">Expenses</option>
              <option value="food">Food</option>
            </select>
          </div>
        </FieldRow>
        <FieldRow label="Deductable">
          <div className="flex gap-2">
            <Input type="number" placeholder="Amount" value={row.deduct_amount} onChange={(e) => setRow((p) => ({ ...p, deduct_amount: e.target.value }))} data-testid="pay-deduct-amount" />
            <select className="h-9 rounded-md border border-input px-2 text-sm" value={row.deduct_type} onChange={(e) => setRow((p) => ({ ...p, deduct_type: e.target.value }))} data-testid="pay-deduct-type">
              <option value="">Type</option>
              <option value="loan">Loan</option>
              <option value="advance">Advance</option>
            </select>
          </div>
        </FieldRow>
        <FieldRow label="Payment method"><PayMethodChecks value={row.payment_method} onChange={(v) => setRow((p) => ({ ...p, payment_method: v }))} testidPrefix="pay-method" /></FieldRow>
        <FieldRow label="Note"><Input placeholder="Note" value={row.note} onChange={(e) => setRow((p) => ({ ...p, note: e.target.value }))} data-testid="pay-note" /></FieldRow>
        <div className="flex items-end"><Button onClick={add} className="bg-ink hover:bg-black w-full sm:w-auto" data-testid="pay-add">Add entry</Button></div>
      </div>
      {entries.length === 0 ? <p className="text-sm text-zinc-400">No payroll entries.</p> : (
        <div className="divide-y divide-zinc-100 text-sm">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center justify-between py-2" data-testid={`pay-${e.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-zinc-500">{e.date}</span>
                <Badge variant="outline" className="text-[10px]">{payTypeLabel(e.type)}</Badge>
                {e.quantity != null ? <span>{e.quantity} {e.unit || "days"}</span>
                  : e.period ? <span className="text-zinc-500">{e.period}</span>
                  : e.hours ? <span>{e.hours}h</span> : null}
                {e.amount != null ? <span className="font-medium">{e.amount}</span> : null}
                {e.additional_amount != null && e.additional_amount !== 0 ? (
                  <span className="text-emerald-700">+{e.additional_amount}{e.additional_type ? ` ${titleCase(e.additional_type)}` : ""}</span>
                ) : null}
                {e.deduct_amount != null && e.deduct_amount !== 0 ? (
                  <span className="text-red-600">−{e.deduct_amount}{e.deduct_type ? ` ${titleCase(e.deduct_type)}` : ""}</span>
                ) : null}
                {e.payment_method
                  ? <Badge className="bg-zinc-100 text-zinc-700">{paymentMethodLabel(e.payment_method)}</Badge>
                  : e.payment_status ? <Badge className={e.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{e.payment_status}</Badge> : null}
                {e.requires_confirmation && (e.confirmed
                  ? <Badge className="bg-emerald-100 text-emerald-700" data-testid={`pay-received-${e.id}`}>Received{e.confirmed_at ? ` · ${String(e.confirmed_at).slice(0, 10)}` : ""}</Badge>
                  : <Badge className="bg-amber-100 text-amber-700" data-testid={`pay-awaiting-${e.id}`}>Awaiting confirmation</Badge>)}
                {e.note ? <span className="text-zinc-400">{e.note}</span> : null}
              </div>
              <button className="text-red-500" onClick={() => del(e.id)}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
